import { ThreadEditingOverlayStageConfig } from 'components/threadEditingOverlay/stage';
import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { withEffects } from 'mixins/withEffects';
import { Person } from 'models/person';
import { TopicThread } from 'models/topicThread';
import { extendedAppContainer } from '../configuration/inversify/container/extended';
import { CurrentUserManager } from './currentUser';
import device from './device';
import { IWCCStorageManager } from './iStorage';
import overlayManager from './overlay';
import SimpleTopicManager from './simpleTopic';
import CommentsManager from './topic/comments';
import ActivityFormManager, { ActivityFormEditorConfig } from './topic/form';

components.preload('thread-editing-overlay-stage');

@injectable()
class ThreadEditingOverlay {
    constructor(
        @inject(wccModules.storage) private storage: IWCCStorageManager
        ) { }

    edit(thread: TopicThread) {
        const effects = withEffects();

        const activityFormManager = effects.register(new ActivityFormManager(settings.discussionId, thread.topicId, { isMobile: device.isMobile, thread }));
        const formEditorConfig = activityFormManager.getEditorConfig('form');

        overlayManager.toOverlay('<thread-editing-overlay-stage params="model: $data" />', {
            lock: true,
            data: stage => {
                return <ThreadEditingOverlayStageConfig>{
                    editorConfig: formEditorConfig.pluck(config => (<ActivityFormEditorConfig>{
                        ...config,

                        onSave: async jsonThreads => {
                            config.onSave?.(jsonThreads);
                            await this.editNextThreadIfAllowed(thread);
                            stage.close();
                        },

                        onCancel: () => {
                            config.onCancel?.();
                            stage.close();
                        }
                    }))
                }
            },

            onClose: () => {
                effects.dispose();
            }
        });
    }

    private async editNextThreadIfAllowed(thread: TopicThread) {
        const userManager = this.storage.getInstance(CurrentUserManager, {});
        const topicManager = this.storage.getInstance(SimpleTopicManager, { topicId: thread.topicId() });

        if (userManager != undefined && topicManager != undefined) {
            const user = await userManager.person.whenNotNull();
            const topic = await topicManager.topic.whenNotNull();

            const threadCreator = Person.fromThread(thread);

            const shouldContinue = settings.allowContinuousSurveyEditing() &&
                topic.isSurvey()
                user.isModerator() &&
                threadCreator.isRegular();

            if (shouldContinue) {
                const commentsManager = this.storage.getInstance(CommentsManager, { topicId: thread.topicId() });

                if (commentsManager != undefined) {
                    const nextThread = await this.getNextParticipantComment(commentsManager, thread);

                    if (nextThread != undefined)
                        this.edit(nextThread);
                }
            }
        }
    }

    private async getNextParticipantComment(commentsManager: CommentsManager, thread: TopicThread): Promise<TopicThread | undefined> {
        await commentsManager.busy.when(v => !v);

        const comments = commentsManager.comments();
        const idx = comments.indexOf(thread);

        if (idx == -1 && !commentsManager.allPagesLoaded()) {
            commentsManager.loadMore();
            return await this.getNextParticipantComment(commentsManager, thread);
        } else if (idx != -1) {
            const targetComments = comments.slice(idx + 1);
            const nextThread = targetComments.find(c => !c.isModerator() && !c.isObserver());

            if (nextThread == undefined && !commentsManager.allPagesLoaded()) {
                commentsManager.loadMore();
                return await this.getNextParticipantComment(commentsManager, thread);
            } else {
                return nextThread;
            }
        }
    }
}

const threadEditingOverlay = extendedAppContainer.resolve(ThreadEditingOverlay);

export default threadEditingOverlay;